/**
 * Destinataires des notifications, par sujet. Deux façons d'abonner, cumulables :
 *
 * 1. NOMINATIVE : matrice utilisateurs x sujets, une case cochée abonne la personne (cloche + e-mail).
 * 2. PAR RÔLE (= par permission) : matrice rôles x sujets. Un rôle abonné vaut abonnement de TOUS
 *    ses porteurs, résolus au moment de l'envoi. Un arrivant qui reçoit le rôle est abonné d'office,
 *    et un départ cesse de l'être : plus rien à maintenir à la main.
 *
 * Chaque bascule ré-enregistre le jeu COMPLET du sujet pour SA matrice (les deux listes sont
 * indépendantes côté API). Repliée par défaut ; les lignes utilisateurs suivent le filtre de la
 * page Utilisateurs via `visibleEmails` (absent ou null = pas de filtre actif).
 * Requiert feature:users:write.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { notificationsClient } from "../../Pages/Notifications/notificationsClient";
import { getUserDirectory, type DirectoryEntry } from "../Account/accountClient";
import { CollapsibleCard } from "../CollapsibleCard";

/** Les sujets abonnables, avec leur libellé FR (colonnes des matrices). */
export const NOTIFICATION_TOPICS: Array<{ key: string; label: string }> = [
	{ key: "contact-messages", label: "Messages de contact" },
	{ key: "backup-errors", label: "Erreurs de sauvegarde" },
	{ key: "redirect-tasks", label: "Redirections à corriger" }
];

/** Un rôle abonnable : la clé est la clé NATURELLE du rôle, jamais un identifiant de ligne. */
export interface SubscribableRole {
	key: string;
	name: string;
}

const key = (topic: string, subject: string) => `${topic}|${subject.toLowerCase()}`;

export const NotificationRecipientsCard: React.FC<{
	visibleEmails?: ReadonlySet<string> | null;
	roles?: SubscribableRole[];
}> = ({ visibleEmails, roles = [] }) => {
	const [directory, setDirectory] = useState<DirectoryEntry[]>([]);
	const [subscribed, setSubscribed] = useState<Set<string>>(new Set());
	const [roleSubscribed, setRoleSubscribed] = useState<Set<string>>(new Set());
	const [saving, setSaving] = useState<string | null>(null);

	const load = async () => {
		const [dir, subs] = await Promise.all([
			getUserDirectory().catch(() => []),
			notificationsClient.subscriptions().catch(() => [])
		]);
		setDirectory(dir);
		const nextEmails = new Set<string>();
		const nextRoles = new Set<string>();
		for (const s of subs) {
			for (const e of s.emails) nextEmails.add(key(s.topic, e));
			for (const r of s.roles ?? []) nextRoles.add(key(s.topic, r));
		}
		setSubscribed(nextEmails);
		setRoleSubscribed(nextRoles);
	};

	useEffect(() => {
		void load();
	}, []);

	const users = useMemo(
		() => directory.filter((d) => !!d.email).map((d) => ({ email: (d.email as string).toLowerCase(), name: d.name || d.email })),
		[directory]
	);

	// Lignes visibles = périmètre du filtre de la page. L'enregistrement porte toujours sur le jeu
	// COMPLET du sujet : un abonné masqué par le filtre n'est jamais désabonné par mégarde.
	const visibles = useMemo(
		() => (visibleEmails ? users.filter((u) => visibleEmails.has(u.email)) : users),
		[users, visibleEmails]
	);

	const toggle = async (topic: string, email: string) => {
		const next = new Set(subscribed);
		const k = key(topic, email);
		if (next.has(k)) next.delete(k);
		else next.add(k);
		setSubscribed(next);
		const emailsForTopic = users.filter((u) => next.has(key(topic, u.email))).map((u) => u.email);
		setSaving(topic);
		try {
			await notificationsClient.setSubscription(topic, emailsForTopic);
		} catch {
			toast.error("Échec de l'enregistrement des destinataires de notifications.");
			setSubscribed(subscribed); // revert
		} finally {
			setSaving(null);
		}
	};

	const toggleRole = async (topic: string, roleKey: string) => {
		const next = new Set(roleSubscribed);
		const k = key(topic, roleKey);
		if (next.has(k)) next.delete(k);
		else next.add(k);
		setRoleSubscribed(next);
		const rolesForTopic = roles.filter((r) => next.has(key(topic, r.key))).map((r) => r.key);
		setSaving(topic);
		try {
			await notificationsClient.setSubscriptionRoles(topic, rolesForTopic);
		} catch {
			toast.error("Échec de l'enregistrement des rôles abonnés.");
			setRoleSubscribed(roleSubscribed); // revert
		} finally {
			setSaving(null);
		}
	};

	if (users.length === 0) return null;

	return (
		<CollapsibleCard
			title="Destinataires des notifications"
			subtitle="Abonnez des personnes, ou directement un rôle : tous ses porteurs reçoivent alors le sujet."
			count={visibles.length}
		>
			<h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">Par personne</h4>
			<p className="mt-0.5 text-xs text-slate-500">
				Les utilisateurs cochés reçoivent les notifications du sujet (cloche de l'admin et e-mail).
			</p>
			{visibles.length === 0 ? (
				<p className="mt-3 text-sm text-slate-500">Aucun utilisateur ne correspond au filtre en cours.</p>
			) : (
				<div className="mt-3 overflow-x-auto">
					<table className="min-w-full text-sm">
						<thead>
							<tr className="border-b border-slate-100 text-left text-xs text-slate-500">
								<th className="py-2 pr-4 font-semibold">Utilisateur</th>
								{NOTIFICATION_TOPICS.map((t) => (
									<th key={t.key} className="px-2 py-2 text-center font-semibold">{t.label}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{visibles.map((u) => (
								<tr key={u.email} className="border-b border-slate-50">
									<td className="py-2 pr-4">
										<span className="font-medium text-slate-700">{u.name}</span>
										<span className="ml-2 text-xs text-slate-400">{u.email}</span>
									</td>
									{NOTIFICATION_TOPICS.map((t) => (
										<td key={t.key} className="px-2 py-2 text-center">
											<input
												type="checkbox"
												aria-label={`${u.email} : ${t.label}`}
												checked={subscribed.has(key(t.key, u.email))}
												disabled={saving === t.key}
												onChange={() => toggle(t.key, u.email)}
											/>
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{roles.length > 0 && (
				<div className="mt-6 border-t border-slate-100 pt-4">
					<h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">Par rôle</h4>
					<p className="mt-0.5 text-xs text-slate-500">
						Tout porteur du rôle coché reçoit le sujet, y compris un arrivant à qui le rôle est attribué plus tard.
						Retirer le rôle à quelqu'un le désabonne aussitôt.
					</p>
					{/* Les roles sont nombreux : la matrice defile dans sa propre zone plutot que
					    d'allonger la page. */}
					<div className="mt-3 max-h-96 overflow-auto">
						<table className="min-w-full text-sm">
							<thead className="sticky top-0 bg-white">
								<tr className="border-b border-slate-100 text-left text-xs text-slate-500">
									<th className="py-2 pr-4 font-semibold">Rôle</th>
									{NOTIFICATION_TOPICS.map((t) => (
										<th key={t.key} className="px-2 py-2 text-center font-semibold">{t.label}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{roles.map((r) => (
									<tr key={r.key} className="border-b border-slate-50">
										<td className="py-2 pr-4">
											<span className="font-medium text-slate-700">{r.name}</span>
											<span className="ml-2 text-xs text-slate-400">{r.key}</span>
										</td>
										{NOTIFICATION_TOPICS.map((t) => (
											<td key={t.key} className="px-2 py-2 text-center">
												<input
													type="checkbox"
													aria-label={`${r.key} : ${t.label}`}
													checked={roleSubscribed.has(key(t.key, r.key))}
													disabled={saving === t.key}
													onChange={() => toggleRole(t.key, r.key)}
												/>
											</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}
		</CollapsibleCard>
	);
};

export default NotificationRecipientsCard;
